import { useCallback, useEffect, useRef, useState } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../contexts/ThemeContext'
import { useCustomerScope } from '../../contexts/CustomerScopeContext'
import { LogoMerke, LogoOrdmerke } from '../Logo'
import NotificationBell from './NotificationBell'
import { StjerneIkon } from './FavorittStjerne'
import { getNavSections } from './menypunkter'
import { useMenyvalgKontekst } from './menyvalgKontekst'
import {
  MIN_SIDE, erAktivtPunkt, erSkrivefelt, favorittpunkter, filtrerMeny, normaliserSok, veksle,
} from '../../lib/meny'

// Menyen er laget etter menyen i Amo Ctrl (Håvard 16.10.2026, tatt inn i
// Pulse 18.10.2026):
//
// - På PC er den smal (bare ikoner) og glir ut over innholdet når musa er over
//   den, uten å flytte siden. Nåla fester den åpen, og da tar den plass som
//   før. Valget huskes i nettleseren (`pulse.meny.festet`). Kundene har den
//   festet til de velger noe annet.
// - Under lg (1024 px) er den en skuff som før. Der finnes ingen smal meny, og
//   nåla er en lukkeknapp.
// - Søket står nederst. «/» hopper dit fra hvor som helst, Enter går til
//   første treff og Escape tømmer.
// - Favoritter står øverst: Min side først, så sidene brukeren har merket med
//   stjerna (her i menyen eller ved sidetittelen). Gruppene kan legges sammen.
//   Begge deler lagres per bruker på serveren (Menyvalg.jsx).
//
// Smal meny har ikke plass til et rullefelt; den ruller med hjulet.
//
// Det som har ulik høyde i smal og bred meny, står under menypunktene, og
// skillestreken i smal meny er like høy som gruppeoverskriften. Da står hvert
// punkt på samme høyde når menyen glir ut, og musa peker fortsatt på det
// samme punktet. En sammenlagt gruppe er derfor sammenlagt i smal meny også.

const FESTET = 'pulse.meny.festet'
// Tailwinds lg: der menyen slutter å være en skuff.
const DESKTOP = '(min-width: 1024px)'
// Litt forsinkelse inn og ut, så menyen ikke blafrer når musa bare passerer kanten.
const INN_MS = 120
const UT_MS = 250

const UTEN_MENYVALG = {
  favoritter: [], lukkede: [], settFavoritter: () => {}, settLukkede: () => {}, aktiv: false, kanEndre: false,
}

const GRUPPEHOYDE = 'h-[34px]'
const GRUPPETEKST = 'text-[10px] font-semibold uppercase tracking-widest text-text-muted/50'

// Uten lagret valg er menyen smal, men for kundene festet: de har seks punkter
// og har ikke brukt den smale menyen i Amo Ctrl.
function lesFestet(standard) {
  try {
    const lagret = localStorage.getItem(FESTET)
    return lagret == null ? standard : lagret === '1'
  } catch { return standard }
}

function useErDesktop() {
  const [desktop, settDesktop] = useState(() => (
    typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(DESKTOP).matches
  ))
  useEffect(() => {
    const mq = window.matchMedia?.(DESKTOP)
    if (!mq) return undefined
    const h = e => settDesktop(e.matches)
    mq.addEventListener?.('change', h)
    return () => mq.removeEventListener?.('change', h)
  }, [])
  return desktop
}

const byggetid = new Date(__BUILD_DATE__).toLocaleString('nb-NO', {
  timeZone: 'Europe/Oslo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
})

export default function Sidebar({ mobileOpen, onMobileClose }) {
  const { role, appUser, signOut, hasPermission, impersonating } = useAuth()
  const { theme, toggleTheme } = useTheme()
  const { customers, scopedCustomerId, setScopedCustomerId } = useCustomerScope()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const meny = useMenyvalgKontekst() || UTEN_MENYVALG
  const navSections = getNavSections(role, hasPermission)
  const showCustomerScope = !impersonating && customers.length > 0
    && (role === 'administrator' || role === 'leder' || role === 'ansatt')

  // --- Smal, flytende eller festet ---
  const desktop = useErDesktop()
  const [festet, settFestet] = useState(() => lesFestet(role === 'kunde'))
  const [over, settOver] = useState(false)          // musa er over menyen
  const [feltFokus, settFeltFokus] = useState(false) // søket eller kundefilteret har fokus
  const tid = useRef(0)
  const asideRef = useRef(null)
  const flytende = desktop && !festet && (over || feltFokus)
  const smal = desktop && !festet && !flytende

  const musInn = () => {
    if (!desktop || festet) return
    clearTimeout(tid.current)
    tid.current = setTimeout(() => settOver(true), INN_MS)
  }
  const musUt = () => {
    clearTimeout(tid.current)
    tid.current = setTimeout(() => settOver(false), UT_MS)
  }
  useEffect(() => () => clearTimeout(tid.current), [])

  // Et felt med fokus holder menyen ute, så den ikke trekker seg sammen midt i
  // et søk eller mens kundelista er åpen.
  const vedFokus = e => { if (erSkrivefelt(e.target)) settFeltFokus(true) }
  const vedBlur = e => {
    if (!erSkrivefelt(e.target)) return
    settFeltFokus(false)
    // Åpnet med «/» mens musa sto utenfor, kommer det aldri noen «mus ut».
    if (!asideRef.current?.matches(':hover')) musUt()
  }

  function vekslFestet() {
    if (!desktop) { onMobileClose?.(); return }
    const neste = !festet
    settFestet(neste)
    try { localStorage.setItem(FESTET, neste ? '1' : '0') } catch { /* privat modus */ }
    if (!neste) settOver(false)
  }

  // Smal meny har ikke plass til feltene. Ikonet (og «/») lar menyen gli ut,
  // og fokuset settes når feltet er tegnet.
  const fokusEtterVisning = useRef(null)
  const sokRef = useRef(null)
  const kundefilterRef = useRef(null)
  const sokFelt = useCallback(el => {
    sokRef.current = el
    if (el && fokusEtterVisning.current === 'sok') { fokusEtterVisning.current = null; el.focus() }
  }, [])
  const kundefilterFelt = useCallback(el => {
    kundefilterRef.current = el
    if (el && fokusEtterVisning.current === 'kundefilter') { fokusEtterVisning.current = null; el.focus() }
  }, [])
  const aapneFelt = useCallback((hvilket, ref) => {
    if (ref.current) { ref.current.focus(); return }
    fokusEtterVisning.current = hvilket
    settOver(true)
  }, [])
  const aapneSok = useCallback(() => aapneFelt('sok', sokRef), [aapneFelt])

  // --- Søket ---
  // Kundene har en kort meny og får ikke søk eller favoritter.
  const kanSoke = role !== 'kunde'
  // Søket hører til siden det ble skrevet på: etter et sidebytte er hele menyen tilbake.
  const [sokTilstand, settSokTilstand] = useState({ tekst: '', sti: pathname })
  const sok = sokTilstand.sti === pathname ? sokTilstand.tekst : ''
  const settSok = tekst => settSokTilstand({ tekst, sti: pathname })

  // «/» hopper til søket, men aldri mens brukeren skriver i et felt, der
  // skråstreken er et vanlig tegn. En lukket skuff (mobil) åpnes ikke.
  useEffect(() => {
    if (!kanSoke) return undefined
    const h = e => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return
      if (erSkrivefelt(document.activeElement)) return
      if (!desktop && !mobileOpen) return
      e.preventDefault()
      aapneSok()
    }
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [kanSoke, desktop, mobileOpen, aapneSok])

  // Punktene brukeren ser, er alt filtrert på tilgang. Søket filtrerer bare
  // videre, så det kan aldri vise noe brukeren ellers ikke ser.
  const soker = kanSoke && normaliserSok(sok) !== ''
  const viste = soker ? filtrerMeny(navSections, sok) : navSections
  const antallTreff = viste.reduce((n, s) => n + s.items.length, 0)

  // --- Favoritter og sammenlagte grupper ---
  // Under søk vises menyen uten Favoritter, så samme treff ikke står to ganger.
  const medFavoritter = meny.aktiv && role !== 'kunde'
  const favoritter = medFavoritter && !soker ? favorittpunkter(navSections, meny.favoritter) : []
  const grupper = favoritter.length
    ? [{ label: 'Favoritter', favoritter: true, items: favoritter }, ...viste]
    : viste
  const iFavoritter = new Set(favoritter.map(it => it.path))
  // Stjerna i menyen vises når musa er over punktet, i bred meny på PC.
  const kanStjerne = medFavoritter && meny.kanEndre && desktop && !smal

  function velgPunkt() {
    settSok('')
    onMobileClose?.()
  }

  function sokTast(e) {
    if (e.key === 'Escape') {
      e.preventDefault()
      if (sok) settSok('')
      else e.currentTarget.blur()
    } else if (e.key === 'Enter') {
      const forste = viste[0]?.items[0]
      if (!forste) return
      e.preventDefault()
      e.currentTarget.blur()
      velgPunkt()
      navigate(forste.path)
    }
  }

  const valgtKunde = customers.find(c => String(c.id) === String(scopedCustomerId))
  const kundefilterTekst = valgtKunde ? `Kundefilter: ${valgtKunde.name}` : 'Kundefilter: alle kunder'

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={onMobileClose} />
      )}

      {/* Plassen menyen tar i raden på PC. Ufestet er den alltid smal: når
          menyen glir ut, legger den seg over innholdet. */}
      <div aria-hidden="true" className={`hidden lg:block shrink-0 transition-[width] duration-150 ${festet ? 'w-60' : 'w-16'}`} />

      <aside
        ref={asideRef}
        aria-label="Meny"
        onMouseEnter={musInn}
        onMouseLeave={musUt}
        onFocus={vedFokus}
        onBlur={vedBlur}
        className={`
          fixed left-0 ${impersonating ? 'top-9 h-[calc(100dvh-2.25rem)]' : 'top-0 h-dvh'}
          z-50 lg:z-40 w-60 ${smal ? 'lg:w-16' : ''}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0
          bg-nav flex flex-col overflow-hidden border-r border-border
          transition-[width,transform,box-shadow] duration-150 ease-out
          ${flytende ? 'shadow-2xl' : ''}
        `}
      >
        {/* Logo i toppen, med varselbjella og nåla ved siden av når menyen er bred.
            Smal ligger bjella nederst, sammen med de andre knappene. */}
        <div className={`h-[68px] shrink-0 border-b border-border flex items-center ${smal ? 'justify-center px-2' : 'justify-between gap-2 px-4'}`}>
          {smal
            ? <LogoMerke tema={theme} className="w-9 h-9 select-none" />
            : <LogoOrdmerke tema={theme} className="h-6 w-auto select-none" />}
          {!smal && (
            <div className="flex items-center gap-1">
              {role !== 'kunde' && <NotificationBell onNavigate={onMobileClose} />}
              <button
                type="button"
                onClick={vekslFestet}
                title={!desktop ? 'Lukk menyen' : festet ? 'Løsne menyen (trekker seg sammen når musa går ut)' : 'Fest menyen åpen'}
                aria-label={!desktop ? 'Lukk menyen' : festet ? 'Løsne menyen' : 'Fest menyen'}
                aria-pressed={desktop ? festet : undefined}
                className={`w-9 h-9 grid place-items-center rounded-lg transition-colors hover:bg-surface ${
                  desktop && festet ? 'text-secondary' : 'text-text-muted hover:text-text'
                }`}
              >
                {desktop ? <NaalIkon className="w-4 h-4" /> : <LukkIkon className="w-5 h-5" />}
              </button>
            </div>
          )}
        </div>

        {/* Like høy smal og bred. Smal er den et merke som viser om et filter er valgt. */}
        {showCustomerScope && (
          <div className="h-16 shrink-0 px-3 flex flex-col justify-center">
            {smal ? (
              <button
                type="button"
                onClick={() => aapneFelt('kundefilter', kundefilterRef)}
                title={kundefilterTekst}
                aria-label={kundefilterTekst}
                className="relative mx-auto w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors"
              >
                <FilterIkon className="w-5 h-5" />
                {valgtKunde && <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-secondary" aria-hidden="true" />}
              </button>
            ) : (
              <>
                <label htmlFor="meny-kundefilter" className="text-[10px] uppercase tracking-widest text-text-muted/50 block mb-1">Kundefilter</label>
                <select
                  id="meny-kundefilter"
                  ref={kundefilterFelt}
                  value={scopedCustomerId || ''}
                  onChange={e => setScopedCustomerId(e.target.value || null)}
                  className="w-full h-8 px-2 bg-bg border border-border rounded text-text text-xs"
                >
                  <option value="">Alle kunder</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </>
            )}
          </div>
        )}

        <nav aria-label="Hovedmeny" className={`flex-1 py-2 overflow-y-auto overflow-x-hidden ${smal ? '[scrollbar-width:none]' : '[scrollbar-width:thin]'}`}>
          {soker && !antallTreff && (
            <p className="px-4 py-3 text-xs text-text-muted">Ingen menypunkter matcher «{sok.trim()}»</p>
          )}
          {grupper.map((seksjon, si) => {
            // En sammenlagt gruppe viser fortsatt siden man står på. Under søk vises alt.
            const lukket = Boolean(seksjon.label) && !soker && meny.lukkede.includes(seksjon.label)
            // Står siden i Favoritter, markeres den bare der, ikke i sin vanlige gruppe også.
            const dempet = it => !seksjon.favoritter && iFavoritter.has(it.path)
            const synlige = lukket
              ? seksjon.items.filter(it => erAktivtPunkt(it, pathname) && !dempet(it))
              : seksjon.items
            return (
              <div key={seksjon.favoritter ? 'favoritter' : (seksjon.label || si)}>
                {seksjon.label && (smal ? (
                  <div className={`${GRUPPEHOYDE} flex items-center justify-center`} aria-hidden="true">
                    <div className="w-6 border-t border-border" />
                  </div>
                ) : soker ? (
                  <p className={`${GRUPPEHOYDE} px-4 pb-1 flex items-end ${GRUPPETEKST}`}>{seksjon.label}</p>
                ) : (
                  <button
                    type="button"
                    onClick={() => meny.settLukkede(l => veksle(l, seksjon.label))}
                    aria-expanded={!lukket}
                    title={lukket ? `Vis ${seksjon.label}` : `Legg sammen ${seksjon.label}`}
                    className={`${GRUPPEHOYDE} w-full px-4 pb-1 flex items-end justify-between gap-2 ${GRUPPETEKST} hover:text-text-muted transition-colors`}
                  >
                    <span>{seksjon.label}</span>
                    <PilIkon className={`w-3 h-3 mb-0.5 shrink-0 transition-transform ${lukket ? '-rotate-90' : ''}`} />
                  </button>
                ))}

                {synlige.map(item => {
                  const ItemIcon = item.icon
                  const medStjerne = kanStjerne && item.path !== MIN_SIDE
                  const erFavoritt = meny.favoritter.includes(item.path)
                  const stjernetekst = erFavoritt ? `Fjern ${item.label} fra favoritter` : `Legg ${item.label} i favoritter`
                  return (
                    <div key={item.path} className="relative group/punkt mx-2">
                      <NavLink
                        to={item.path}
                        end={item.path === '/' || item.path === '/kunde'}
                        onClick={velgPunkt}
                        className={({ isActive }) =>
                          `flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${medStjerne ? 'pr-9' : ''} ${
                            isActive && !dempet(item)
                              ? 'bg-secondary/15 text-secondary'
                              : 'text-text-muted hover:bg-surface hover:text-text'
                          }`
                        }
                        title={smal ? item.label : undefined}
                      >
                        <ItemIcon className="w-5 h-5 shrink-0" />
                        {!smal && <span className="truncate">{item.label}</span>}
                      </NavLink>
                      {medStjerne && (
                        <button
                          type="button"
                          onClick={() => meny.settFavoritter(f => veksle(f, item.path))}
                          title={stjernetekst}
                          aria-label={stjernetekst}
                          aria-pressed={erFavoritt}
                          className={`absolute right-1.5 top-1/2 -translate-y-1/2 w-6 h-6 grid place-items-center rounded-md opacity-0 group-hover/punkt:opacity-100 focus-visible:opacity-100 transition-opacity ${
                            erFavoritt ? 'text-accent' : 'text-text-muted hover:text-text'
                          }`}
                        >
                          <StjerneIkon fylt={erFavoritt} className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          })}
        </nav>

        {/* Søket står nederst, over brukeren, som i Amo Ctrl. */}
        {kanSoke && (
          <div className={`shrink-0 border-t border-border py-2 ${smal ? 'px-2' : 'px-3'}`}>
            {smal ? (
              <button
                type="button"
                onClick={aapneSok}
                title="Søk i menyen (/)"
                aria-label="Søk i menyen"
                className="w-full h-9 grid place-items-center rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors"
              >
                <SokIkon className="w-5 h-5" />
              </button>
            ) : (
              <div className="flex items-center gap-2 h-9 px-2.5 rounded-lg border border-border bg-bg text-text-muted focus-within:border-primary transition-colors">
                <SokIkon className="w-4 h-4 shrink-0" />
                <input
                  ref={sokFelt}
                  type="search"
                  value={sok}
                  onChange={e => settSok(e.target.value)}
                  onKeyDown={sokTast}
                  placeholder="Søk i menyen …"
                  aria-label="Søk i menyen"
                  autoComplete="off"
                  spellCheck={false}
                  className="flex-1 min-w-0 bg-transparent border-0 outline-none text-base lg:text-sm text-text placeholder:text-text-muted [&::-webkit-search-cancel-button]:hidden"
                />
                {sok ? (
                  <button
                    type="button"
                    onClick={() => { settSok(''); sokRef.current?.focus() }}
                    title="Tøm søket"
                    aria-label="Tøm søket"
                    className="w-6 h-6 grid place-items-center rounded text-text-muted hover:text-text"
                  >
                    <LukkIkon className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <kbd aria-hidden="true" className="hidden lg:inline-block font-sans text-[11px] leading-none px-1.5 py-1 rounded border border-border">/</kbd>
                )}
              </div>
            )}
            <span className="sr-only" aria-live="polite">{soker ? `${antallTreff} treff` : ''}</span>
          </div>
        )}

        {/* User info + sign out */}
        <div className="shrink-0 p-3 border-t border-border">
          {!smal ? (
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <span className="text-primary text-xs font-bold">
                  {(appUser?.name || appUser?.email || '?')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-text text-xs font-medium truncate">{appUser?.name || appUser?.email}</p>
                <p className="text-text-muted text-[10px] capitalize">{role}</p>
              </div>
              <button type="button" onClick={toggleTheme} className="w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors" title={theme === 'dark' ? 'Lys modus' : 'Mørk modus'} aria-label={theme === 'dark' ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'}>
                {theme === 'dark' ? <SunIcon className="w-4 h-4" /> : <MoonIcon className="w-4 h-4" />}
              </button>
              <button type="button" onClick={() => navigate('/reset-password')} className="w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors" title="Bytt passord" aria-label="Bytt passord">
                <KeyIcon className="w-4 h-4" />
              </button>
              <button type="button" onClick={signOut} className="w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-danger hover:bg-surface transition-colors" title="Logg ut" aria-label="Logg ut">
                <LogoutIcon className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              {role !== 'kunde' && <NotificationBell collapsed onNavigate={onMobileClose} />}
              <button type="button" onClick={toggleTheme} className="w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors" title={theme === 'dark' ? 'Lys modus' : 'Mørk modus'} aria-label={theme === 'dark' ? 'Bytt til lyst tema' : 'Bytt til mørkt tema'}>
                {theme === 'dark' ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
              </button>
              <button type="button" onClick={() => navigate('/reset-password')} className="w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-text hover:bg-surface transition-colors" title="Bytt passord" aria-label="Bytt passord">
                <KeyIcon className="w-5 h-5" />
              </button>
              <button type="button" onClick={signOut} className="w-9 h-9 grid place-items-center rounded-lg text-text-muted hover:text-danger hover:bg-surface transition-colors" title="Logg ut" aria-label="Logg ut">
                <LogoutIcon className="w-5 h-5" />
              </button>
            </div>
          )}

          {/* Build-info */}
          {!smal && (
            <p className="mt-2 min-w-0 truncate text-[10px] text-text-muted/50 select-none" title={`Siste build: ${byggetid} (${__BUILD_SHA__})`}>
              <span className="font-mono">{__BUILD_SHA__}</span> · {byggetid}
            </p>
          )}
        </div>
      </aside>
    </>
  )
}

// --- Ikoner ---
function LogoutIcon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9" /></svg>
}
function KeyIcon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 0 1 3 3m3 0a6 6 0 0 1-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1 1 21.75 8.25Z" /></svg>
}
function SunIcon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386-1.591 1.591M21 12h-2.25m-.386 6.364-1.591-1.591M12 18.75V21m-4.773-4.227-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0Z" /></svg>
}
function MoonIcon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.72 9.72 0 0 1 18 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 0 0 3 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 0 0 9.002-5.998Z" /></svg>
}
function SokIkon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>
}
function LukkIkon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" /></svg>
}
function PilIkon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" /></svg>
}
// Nåla (en tegnestift), etter pin-ikonet i Lucide.
function NaalIkon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 17v5M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" /></svg>
}
function FilterIkon({ className }) {
  return <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" /></svg>
}
